import { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import SearchField from "@components/Forms/SearchField/SearchField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import "./styles/styles";
import { faTableLayout } from "@fortawesome/pro-solid-svg-icons";
import { Tab, Tabs } from "react-bootstrap";
import DataTable from "@components/DataTable";
import useListProducts from "./api/useListProducts";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import ControlledDropDownV2 from "@components/Forms/ControlledDropDownV2";
import { Product } from "../types";
import { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "react-router";
import Add from "../Add";
import { useGetAccountQuery } from "@generated";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import CurrencyFormat from "react-currency-format";

const New = (): JSX.Element => {
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [allSelectedProducts, setAllSelectedProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchInput, setSearchInput] = useState<string>("");
  //The following two variables are set to the option.value that it matches in the options array
  const [productSource, setProductSource] = useState<string>("standard");
  const [searchBy, setSearchBy] = useState<string>("name");
  const [onAddPage, setOnAddPage] = useState(false);
  const navigate = useNavigate();

  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const {
    data: account,
    isLoading,
    isError,
  } = useGetAccountQuery({ slug: accountSlug });

  const {
    products,
    error,
    pageMeta: productsMeta,
    isLoading: productsLoading,
    refetch,
  } = useListProducts({
    page: { number: currentPage, size: pageSize },
  });

  useEffect(() => {
    let newSelectedProducts = [...allSelectedProducts];
    selectedProductIds.forEach((id) => {
      const productExistsInSelected = allSelectedProducts.find(
        (product) => Number(product.id) === Number(id)
      );
      const foundProductOnPage = products.find(
        (prod) => Number(prod.id) === Number(id)
      );
      if (!productExistsInSelected) {
        if (foundProductOnPage) {
          newSelectedProducts.push(foundProductOnPage);
        }
      }
    });
    newSelectedProducts = newSelectedProducts.filter((product) =>
      selectedProductIds.includes(product.id.toString())
    );
    setAllSelectedProducts(newSelectedProducts);
  }, [selectedProductIds]);

  const productSourceOptions = [
    { label: "Standard Products", value: "standard" },
    { label: "ConnectWise", value: "connectwise" },
    { label: "Adaptive Catalog", value: "adaptiveCatalog" },
  ];

  const searchByOptions = [
    { label: "Name", value: "name" },
    { label: "Vendor", value: "vendor" },
    { label: "Vendor Part #", value: "partNumber" },
    { label: "Internal Product ID", value: "productId" },
    { label: "Units", value: "units" },
    { label: "Unit Price", value: "unitPrice" },
  ];

  const currencyUnit =
    account &&
    account.data &&
    account.data.attributes &&
    account.data.attributes["field-labels"]["currency_unit"]
      ? account.data.attributes["field-labels"]["currency_unit"]
      : "$";

  const productsTableColumns: ColumnDef<Product>[] = [
    {
      header: "Name/Description",
      cell(props) {
        return (
          <div className="displayFlex">
            {props.row.original.name}{" "}
            {props.row.original.name !== "No products found" && (
              <FontAwesomeIcon className="productPreview" icon={faEye} />
            )}
          </div>
        );
      },
    },
    {
      header: "MFR Part",
      cell(props) {
        return <>{props.row.original.partNumber}</>;
      },
    },
    {
      header: "SKU",
      cell(props) {
        return <>{props.row.original.sku}</>;
      },
    },
    {
      header: "Int Prod Id",
      cell(props) {
        return <>{props.row.original.productId}</>;
      },
    },
    {
      header: "Units",
      cell(props) {
        return <>{props.row.original.units}</>;
      },
    },
    {
      header: "Category",
      cell(props) {
        return <>{props.row.original.category}</>;
      },
    },
    {
      header: "Unit Price",
      cell(props) {
        return (
          <CurrencyFormat
            displayType="text"
            prefix={currencyUnit}
            isNumericString={true}
            thousandSeparator={true}
            value={(props.row.original.unitPrice
              ? Number(props.row.original.unitPrice)
              : 0
            ).toFixed(2)}
          />
        );
      },
    },
  ];

  if (onAddPage) {
    return (
      <Add
        products={allSelectedProducts}
        selectedProductIds={selectedProductIds}
        setSelectedProductIds={setSelectedProductIds}
        setOnAddPage={(bool) => setOnAddPage(bool)}
        currencyUnit={currencyUnit}
      />
    );
  } else {
    return (
      <div className="addProducts">
        <div className="addProductsCard">
          <div className="addProductsHeader">
            <div className="displayFlex">
              <Button
                className={"ssButtonBgWhite"}
                onClick={(e) => {
                  const previousPage = window.location.href
                    .split("/")
                    .slice(0, -1)
                    .join("/");
                  window.location.replace(previousPage);
                }}
              >
                Cancel
              </Button>
              <h2>Search Products</h2>
            </div>
            <Button
              className={`actionButton ${
                selectedProductIds.length === 0 ? "disabled" : ""
              }`}
              onClick={() => setOnAddPage(true)}
            >
              Continue to Add&nbsp;
              {selectedProductIds.length > 0 &&
                `(${selectedProductIds.length})`}
            </Button>
          </div>
          <Form
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                refetch();
              }
            }}
            onSubmit={(e) => {
              e.preventDefault();
              refetch();
            }}
          >
            <div className="searchContainer">
              <ControlledDropDownV2
                label="Product Source"
                value={
                  productSourceOptions.find(
                    (option) => option.value == productSource
                  )?.label || "Standard Products"
                }
                options={productSourceOptions.map((option, i) => (
                  <option key={i} accessKey={option.value}>
                    {option.label}
                  </option>
                ))}
                onChange={(e) => {
                  const productSource = productSourceOptions.find(
                    (option) => option.label == e.target.value
                  );

                  setProductSource(productSource ? productSource.value : "");
                }}
                required
                readOnly={false}
                disabled={false}
              />
              <ControlledDropDownV2
                label="Search By"
                value={
                  searchByOptions.find((option) => option.value == searchBy)
                    ?.label || "Name"
                }
                options={searchByOptions.map((option, i) => (
                  <option key={i} accessKey={option.value}>
                    {option.label}
                  </option>
                ))}
                onChange={(e) => {
                  const searchBy = searchByOptions.find(
                    (option) => option.label == e.target.value
                  );
                  setSearchBy(searchBy ? searchBy.value : "");
                }}
                required
                readOnly={false}
                disabled={false}
              />
              <div className="searchBar">
                <FormFieldLabel label="Search Catalog" className={""} />
                <div className="displayFlex">
                  <SearchField
                    placeholder="Enter search term to get started"
                    onChange={(e) => setSearchInput(e.target.value)}
                    value={searchInput}
                    onClick={() => {
                      setSearchInput("");
                      refetch();
                    }}
                  />
                  <FontAwesomeIcon className="tableIcon" icon={faTableLayout} />
                </div>
              </div>
            </div>
          </Form>

          <div>
            <Tabs defaultActiveKey={1} id="uncontrolled-tab">
              <Tab
                eventKey={1}
                title={`All Products ${
                  productsMeta?.recordCount
                    ? `(${productsMeta.recordCount})`
                    : ""
                }`}
              >
                <>
                  {!products || productsLoading ? (
                    <SmallSpinner />
                  ) : (
                    <DataTable
                      data={
                        products?.length > 0
                          ? products
                          : [
                              {
                                id: 0,
                                name: "No products found",
                                vendor: "",
                                partNumber: "",
                                productId: "",
                                units: "",
                                unitPrice: "",
                              },
                            ]
                      }
                      columns={productsTableColumns}
                      selectable={products?.length > 0}
                      bordered
                      striped
                      hover
                      totalPages={productsMeta.pageCount}
                      totalRows={productsMeta.recordCount}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      pageSize={pageSize}
                      onPageSizeChange={setPageSize}
                      selectedIds={selectedProductIds}
                      setSelectedIds={setSelectedProductIds}
                      paginationEnabled
                      pageSizeEnabled
                    />
                  )}
                </>
              </Tab>
              <Tab eventKey={2} title={`Favorites (0)`}>
                <>Favorites</>
              </Tab>
            </Tabs>
          </div>
        </div>
      </div>
    );
  }
};

export default New;
